import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useFetchBillDetailsLazyQuery } from '../../generated'
import { GRAPHQL_QUERY_POLICY } from '../constants'
import { AuthContext } from '../context'
import { Alert } from '../components/common/Alert'
import { BillDetail } from '../interfaceTypes/common'
import useFetchBillInterest from './useFetchBillInterest'
import { calculateClientBillData } from '../utils/billing'
import useFetchOutstandingBills from './useFetchOutstandingBills'

interface UseFetchBillDetailsProps {
  billId: number | undefined
  selectedClientId: string | undefined
  includeInterest: boolean
  isPrebillView?: boolean
  setInitialClientId?: (clientId: string) => void
}

const useFetchBillDetails = ({
  billId,
  selectedClientId,
  isPrebillView,
  includeInterest,
  setInitialClientId
}: UseFetchBillDetailsProps) => {
  const [bill, setBill] = useState<BillDetail>()
  const { currentUser } = useContext(AuthContext)

  const selectedAccessToken = useMemo(
    () => bill?.accessTokens?.find(token => token.client?.id === selectedClientId),
    [bill?.accessTokens, selectedClientId]
  )

  const interestData = useFetchBillInterest({
    billId: bill?.id,
    billType: bill?.type,
    clientId: selectedClientId,
    token: selectedAccessToken?.token
  })

  const outstandingBillsData = useFetchOutstandingBills({
    caseId: bill?.case?.id,
    billId: bill?.id,
    billStatus: bill?.status,
    billType: bill?.type,
    clientId: selectedClientId,
    token: selectedAccessToken?.token,
    isPrebillView
  })

  const [fetchBillDetails, { refetch, loading, error }] = useFetchBillDetailsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError(error) {
      Alert.error(error.message || 'Failed to fetch bill details')
    },

    onCompleted(data) {
      if (data?.fetchBillDetails) {
        const { bill, response } = data.fetchBillDetails
        const { status } = response || {}

        if (status === 200 && bill) {
          setBill(bill)

          if (bill.accessTokens?.length && !selectedClientId && setInitialClientId) {
            const firstAccessToken = bill.accessTokens[0]

            if (firstAccessToken.client) {
              setInitialClientId(firstAccessToken.client.id)
            }
          }
        }
      }
    }
  })

  const clientData = useMemo(() => {
    const clientWrittenOff =
      (selectedAccessToken?.writtenOffPrincipal || 0) + (selectedAccessToken?.writtenOffInterest || 0)
    const billWrittenOff = (bill?.writtenOffPrincipal || 0) + (bill?.writtenOffInterest || 0)

    return calculateClientBillData({
      paymentAllocations: bill?.paymentAllocations || [],
      showPrebillView: isPrebillView,
      previousBalance: outstandingBillsData?.previousBalance,
      bill: {
        amount: bill?.amount,
        splitBill: !!bill?.case?.billing?.splitBillEnabled,
        status: bill?.status,
        discount: bill?.discount,
        interestAccrued: includeInterest ? interestData.interestAccrued : undefined,
        writtenOff: billWrittenOff
      },
      client: { id: selectedClientId, share: selectedAccessToken?.amount, writtenOff: clientWrittenOff }
    })
  }, [
    bill,
    selectedClientId,
    selectedAccessToken?.amount,
    selectedAccessToken?.writtenOffPrincipal,
    selectedAccessToken?.writtenOffInterest,
    isPrebillView,
    includeInterest,
    interestData.interestAccrued,
    outstandingBillsData?.previousBalance
  ])

  const refetchData = useCallback(() => {
    refetch()
    interestData.refetch()
    outstandingBillsData.refetch()
  }, [refetch, interestData, outstandingBillsData])

  useEffect(() => {
    if (billId && currentUser) {
      fetchBillDetails({
        variables: {
          fetchBillDetailsInput: {
            billId
          }
        }
      })
    }
  }, [billId, currentUser, fetchBillDetails])

  return {
    bill,
    clientData,
    interestData,
    outstandingBillsData,
    refetch: refetchData,
    refetchBillDetails: refetch,
    loading,
    error
  }
}

export default useFetchBillDetails
